/**
 * FounderApply — public landing for founders to submit their pitch.
 *
 * The user requirement: founders can ONLY register themselves with a pitch.
 * The pitch deck URL is mandatory. After submission the AI council
 * automatically runs and a human reviewer emails the founder with one of:
 *
 *   • GREEN  — approved
 *   • AMBER  — improvement program / meeting requested
 *   • RED    — respectfully declined
 *
 * On successful submission the founder is auto-logged in and sent to
 * /dashboard/founder where they can watch the AI council verdict appear.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, ShieldCheck, Sparkles, Mail, Lock, Building, Briefcase, Link2, FileText, Phone } from 'lucide-react';
import Logo, { LogoMark } from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import LinkedInAutofillField from '@/components/LinkedInAutofillField';
import PhoneInput from '@/components/PhoneInput';
import Avatar from '@/components/Avatar';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { BRAND } from '@/lib/constants';

const INDUSTRIES = [
  'FinTech', 'HealthTech', 'ClimateTech', 'AI/ML', 'Enterprise SaaS',
  'ConsumerTech', 'DeepTech', 'Marketplace', 'Hardware', 'EdTech',
  'Real Estate', 'Logistics', 'Aviation', 'AgTech', 'Manufacturing',
  'Energy', 'Maritime', 'Other',
];

function FieldLabel({ icon: Icon, children, required = true }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-600 dark:text-white/70">
      {Icon && <Icon size={12} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/>}
      {children}
      {required && <span className="text-[#5E1F8B] dark:text-[#8B3FC5]">*</span>}
    </div>
  );
}

export default function FounderApply() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    phone_country: null,
    picture: null,
    startup_name: '',
    industry: 'FinTech',
    pitch_deck_url: '',
    pitch_summary: '',
    company: '',
    founder_linkedin_url: '',
    startup_linkedin_url: '',
    consent_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return 'Please tell us your full name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return 'A valid work email is required.';
    if (form.password.length < 8) return 'Password must be ≥ 8 characters.';
    if (!form.startup_name.trim()) return 'What is your startup called?';
    if (!form.pitch_deck_url.match(/^https?:\/\/.+/i))
      return 'A pitch deck link is mandatory — paste a Drive, Dropbox, Notion or Pitch link.';
    if (form.pitch_summary.trim().length < 120)
      return `Pitch summary must be at least 120 characters (you have ${form.pitch_summary.trim().length}).`;
    if (!form.consent_terms) return 'You must accept the terms to apply.';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/vetting/apply', form);
      // Cookie is set by backend; bring the in-memory bearer in sync too.
      if (data?.token) {
        const { setToken } = await import('@/lib/tokenStore');
        setToken(data.token);
        await refresh?.();
      }
      setSubmitted({
        assessment_id: data.assessment_id,
        startup_name: form.startup_name,
        email: form.email,
      });
      toast.success('Your pitch has been submitted. Check your email shortly.');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Could not submit application.');
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#F7F5F4] dark:bg-[#0A0A0A] text-slate-900 dark:text-white flex items-center justify-center p-6">
        <div className="ve-card max-w-xl w-full p-10 text-center" data-testid="founder-apply-success">
          <div className="mx-auto h-16 w-16 rounded-full bg-[#8B3FC5]/15 border border-[#8B3FC5]/40 flex items-center justify-center mb-6">
            <ShieldCheck className="text-[#5E1F8B] dark:text-[#8B3FC5]" size={28}/>
          </div>
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Application received</div>
          <h1 className="display text-3xl mt-3 font-bold">Thank you, {form.name.split(' ')[0]}.</h1>
          <p className="mt-4 text-sm text-slate-600 dark:text-white/70 leading-relaxed">
            <b>{submitted.startup_name}</b> is now in front of our AI council. We&apos;ve sent a
            confirmation to <code className="text-[#7A5A00] dark:text-[#8B3FC5]">{submitted.email}</code>.
            A human reviewer will email you next with one of four outcomes —
            <span className="text-emerald-700 dark:text-emerald-300"> approved</span>,
            <span className="text-amber-700 dark:text-amber-300"> improvement program</span>,
            <span className="text-amber-700 dark:text-amber-300"> meeting requested</span>, or
            <span className="text-rose-700 dark:text-rose-300"> respectfully declined</span>.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              data-testid="founder-apply-go-dashboard"
              onClick={() => nav('/dashboard/founder')}
              className="ve-btn-primary inline-flex items-center gap-2 text-sm">
              Open founder dashboard <ArrowRight size={14}/>
            </button>
            <Link to="/home" className="ve-btn-secondary text-sm">Back to home</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F7F5F4] dark:bg-[#0A0A0A] text-slate-900 dark:text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 ve-glass border-b border-slate-200/60 dark:border-white/10">
        <div className="ve-container flex items-center justify-between h-16">
          <Logo />
          <div className="flex items-center gap-3">
            <ThemeToggle compact />
            <LanguageSwitcher />
            <Link to="/login" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#0A0A0A] dark:text-white/80 hover:text-[#7A5A00] dark:hover:text-[#8B3FC5]">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      <section className="ve-container py-12 md:py-16 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
        {/* Left rail — pitch */}
        <div>
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Founder Application</div>
          <h1 className="display text-3xl md:text-5xl mt-3 font-bold leading-[1.05]">
            Put your venture in front of the <span className="ve-gold-text">Vigilant council</span>.
          </h1>
          <p className="mt-5 text-base text-slate-600 dark:text-white/70 max-w-xl leading-relaxed">
            Founders may only register with a pitch. Once you submit, our AI council
            of three strategist personas — chaired by our Chief Investment Officer —
            grades your pitch on five dimensions and a human reviewer issues a
            final decision by email within 72 hours.
          </p>

          <div className="mt-8 space-y-3 max-w-md">
            {[
              { i: ShieldCheck,   t: 'AI council scorecard',  s: 'Three personas score your pitch in parallel.' },
              { i: Sparkles,      t: 'Human-reviewed verdict', s: 'A senior reviewer signs off before any email is sent.' },
              { i: Mail,          t: 'Clear outcome by email',  s: 'Green, amber or red — never radio silence.' },
            ].map((b) => (
              <div key={b.t} className="flex items-start gap-3 p-3 ve-card">
                <div className="h-9 w-9 rounded-full border border-[#8B3FC5]/30 bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                  <b.i size={15} className="text-[#8B3FC5]"/>
                </div>
                <div>
                  <div className="text-sm font-bold">{b.t}</div>
                  <div className="text-xs text-slate-500 dark:text-white/60 mt-0.5">{b.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <form onSubmit={submit} className="ve-card p-6 md:p-8" data-testid="founder-apply-form">
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Step 1 of 1</div>
          <h2 className="display text-xl font-bold mt-1">Submit your pitch</h2>

          <div className="mt-5 grid gap-4">
            <div className="flex items-center gap-4">
              <Avatar
                src={form.picture}
                email={form.email}
                name={form.name || 'You'}
                size={56}
                testId="founder-avatar"
              />
              <div className="flex-1">
                <FieldLabel>Full Name</FieldLabel>
                <input data-testid="founder-name" required value={form.name} onChange={setField('name')}
                  className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>
              </div>
            </div>

            <div>
              <FieldLabel required={false}>Your LinkedIn (optional — we&apos;ll prefill your name if blank)</FieldLabel>
              <div className="mt-1.5">
                <LinkedInAutofillField
                  value={form.founder_linkedin_url}
                  onChange={(v) => setForm((f) => ({ ...f, founder_linkedin_url: v }))}
                  onAutofill={(fields) => {
                    setForm((f) => ({
                      ...f,
                      name: f.name || fields.name || '',
                      picture: f.picture || fields.picture_url || null,
                    }));
                  }}
                  testId="founder-linkedin"
                  placeholder="https://www.linkedin.com/in/your-handle"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel icon={Mail}>Work Email</FieldLabel>
                <input data-testid="founder-email" type="email" required value={form.email} onChange={setField('email')}
                  className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>
              </div>
              <div>
                <FieldLabel icon={Lock}>Password (≥ 8 chars)</FieldLabel>
                <input data-testid="founder-password" type="password" minLength={8} required value={form.password} onChange={setField('password')}
                  className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>
              </div>
            </div>

            <div>
              <FieldLabel icon={Phone}>Phone Number</FieldLabel>
              <div className="mt-1.5">
                <PhoneInput
                  value={form.phone}
                  country={form.phone_country}
                  required
                  onChange={(phone, iso) => setForm((f) => ({
                    ...f, phone, phone_country: iso,
                  }))}
                  testId="founder-phone"
                />
              </div>
              <p className="mt-1 text-[10px] text-slate-500">
                We&apos;ll text you the council&apos;s decision and any meeting requests.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel icon={Building}>Startup Name</FieldLabel>
                <input data-testid="founder-startup-name" required value={form.startup_name} onChange={setField('startup_name')}
                  className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>
              </div>
              <div>
                <FieldLabel icon={Briefcase}>Industry</FieldLabel>
                <select data-testid="founder-industry" value={form.industry} onChange={setField('industry')}
                  className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]">
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>

            <div>
              <FieldLabel required={false}>Startup LinkedIn page (optional — we&apos;ll prefill name + industry + tagline if blank)</FieldLabel>
              <div className="mt-1.5">
                <LinkedInAutofillField
                  variant="company"
                  value={form.startup_linkedin_url}
                  onChange={(v) => setForm((f) => ({ ...f, startup_linkedin_url: v }))}
                  onAutofill={(fields) => {
                    setForm((f) => ({
                      ...f,
                      startup_name: f.startup_name || fields.name || '',
                      industry: (INDUSTRIES.includes(fields.industry || '')
                        ? fields.industry
                        : f.industry),
                      // Suggest a pitch summary if blank
                      pitch_summary: f.pitch_summary || (
                        [fields.tagline, fields.summary].filter(Boolean).join('\n\n').slice(0, 2000) || ''
                      ),
                    }));
                  }}
                  testId="startup-linkedin"
                  placeholder="https://www.linkedin.com/company/your-startup"
                />
              </div>
            </div>

            <div>
              <FieldLabel icon={Link2}>Pitch Deck Link</FieldLabel>
              <input data-testid="founder-deck-url" type="url" required placeholder="https://drive.google.com/…"
                value={form.pitch_deck_url} onChange={setField('pitch_deck_url')}
                className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>
              <p className="mt-1 text-[10.5px] uppercase tracking-[0.16em] text-slate-500 dark:text-white/55">
                Required · Google Drive / Dropbox / Notion / Pitch / personal site. Make sure it&apos;s publicly viewable.
              </p>
            </div>

            <div>
              <FieldLabel icon={FileText}>Pitch Summary (≥ 120 characters)</FieldLabel>
              <textarea
                data-testid="founder-pitch-summary" required minLength={120} rows={6}
                placeholder="Problem · Solution · Market · Traction · Team · Ask."
                value={form.pitch_summary} onChange={setField('pitch_summary')}
                className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>
              <p className="mt-1 text-[10.5px] uppercase tracking-[0.16em] text-slate-500 dark:text-white/55">
                {form.pitch_summary.length}/120 · The AI council will read this verbatim.
              </p>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="founder-consent" type="checkbox" checked={form.consent_terms}
                onChange={(e) => setForm((f) => ({ ...f, consent_terms: e.target.checked }))}
                className="mt-1"/>
              <span className="text-[12px] text-slate-600 dark:text-white/65 leading-relaxed">
                I confirm the information above is accurate, that I own or have the right to share the
                pitch deck, and I accept the Vigilant Entities NDA terms during evaluation.
              </span>
            </label>

            <button type="submit" disabled={busy} data-testid="founder-submit"
              className="ve-btn-primary justify-center mt-2">
              {busy ? 'Submitting to AI council…' : 'Submit pitch for review'} <ArrowRight size={14}/>
            </button>

            <p className="text-center text-[11px] text-slate-500 dark:text-white/55">
              Already a founder?{' '}
              <Link to="/login" className="text-[#7A5A00] dark:text-[#8B3FC5] font-semibold">Sign in</Link>
            </p>
          </div>
        </form>
      </section>

      <footer className="border-t border-slate-200 dark:border-white/10">
        <div className="ve-container py-6 flex items-center justify-between text-[10.5px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/45">
          <div className="flex items-center gap-2">
            <LogoMark className="h-4 w-4" noLink/>
            <span>Vigilant Entities · {BRAND.city}</span>
          </div>
          <div>© {new Date().getFullYear()} M3 Holdings</div>
        </div>
      </footer>
    </main>
  );
}
